import { useTranslation } from "react-i18next";
import { CalendarCheck, Phone, Heart, Music, GlassWater } from "lucide-react";
import { scrollToSection } from "@/lib/scrollToSection";

const heroImageSrc = "/assets/hero/team-together-960.webp";
const heroImageSrcSet = [
  "/assets/hero/team-together-960.webp 960w",
  "/assets/hero/team-together-1280.webp 1280w",
  "/assets/hero/team-together-1600.webp 1600w",
].join(", ");
const heroImageAvifSrcSet = [
  "/assets/hero/team-together-960.avif 960w",
  "/assets/hero/team-together-1280.avif 1280w",
  "/assets/hero/team-together-1600.avif 1600w",
].join(", ");

export function Hero() {
  const { t } = useTranslation();

  return (
    <section id="hero" className="pt-16 min-h-screen flex items-center relative overflow-hidden" data-testid="hero-section">
      {/* Hero background with gradient */}
      <div className="absolute inset-0 gradient-bg opacity-90"></div>
      
      {/* Team photo as background */}
      <div className="absolute inset-0 overflow-hidden opacity-30" aria-hidden="true">
        <picture className="block h-full w-full">
          <source srcSet={heroImageAvifSrcSet} sizes="100vw" type="image/avif" />
          <source srcSet={heroImageSrcSet} sizes="100vw" type="image/webp" />
          <img
            src={heroImageSrc}
            srcSet={heroImageSrcSet}
            sizes="100vw"
            alt=""
            className="h-full w-full object-cover object-top"
            width="1600"
            height="1068"
            decoding="async"
          />
        </picture>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="font-poppins font-bold text-4xl md:text-6xl lg:text-7xl text-white mb-6" data-testid="hero-title">
            {t('hero.title')} <span className="text-accent">{t('hero.titleHighlight')}</span><br />
            {t('hero.titleEnd')}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto font-light" data-testid="hero-subtitle">
            {t('hero.subtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              className="bg-white text-primary font-semibold px-8 py-4 rounded-full hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-xl"
              data-testid="book-event-button"
              onClick={() => scrollToSection("#contact")}
            >
              <CalendarCheck className="inline w-5 h-5 mr-2" />
              {t('hero.bookEvent')}
            </button>
            <button 
              className="border-2 border-white text-white font-semibold px-8 py-4 rounded-full hover:bg-white hover:text-primary transition-all duration-300 transform hover:scale-105"
              data-testid="contact-button"
              onClick={() => scrollToSection("#contact")}
            >
              <Phone className="inline w-5 h-5 mr-2" />
              {t('hero.contact')}
            </button>
          </div>
        </div>
      </div>

      {/* Floating elements */}
      <div className="absolute top-1/4 left-10 text-white/30 animate-bounce-slow">
        <Heart className="w-10 h-10" />
      </div>
      <div className="absolute bottom-1/3 right-16 text-white/30 animate-pulse-soft">
        <Music className="w-8 h-8" />
      </div>
      <div className="absolute top-1/3 right-1/4 text-white/30 animate-bounce-slow" style={{ animationDelay: '1s' }}>
        <GlassWater className="w-6 h-6" />
      </div>
    </section>
  );
}
